/**
 * offers.js — monthly offers (2026-10).
 *
 * Reads the live offer once per page from the app ({proxy}/public/offers, cached
 * 60 s in sessionStorage) and draws the offer pieces that exist on this page:
 *   top countdown bar · homepage ticket + banner · DJ Samo strip text ·
 *   VIP plan tags · services tags + "offer / normal order" choice ·
 *   DJ Samo page course prices · the sale page (deals, courses, offers you missed).
 * No live offer → nothing is drawn (switching the offer off in the dashboard, or
 * reaching its end date, removes every piece).
 *
 * Free-song credits are separate: they outlive the offer (valid 1 month from the
 * VIP payment), so for a signed-in customer the song page's "Use 1 credit" button
 * and the account card read {proxy}/offers/shopify/credits on their own.
 *
 * All offer text arrives from the dashboard and is escaped before it is drawn.
 */
(function () {
  'use strict';
  var cfg = window.YD_OFFERS;
  if (!cfg || !cfg.proxy) return;
  var urls = cfg.urls || {};
  var AR = cfg.locale === 'ar';
  var CACHE = 'yd.offers.v1';
  var WA = '971544503515';

  /* ---------- helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function safeHref(u) {
    u = String(u || '');
    return /^https:\/\//.test(u) || (/^\//.test(u) && !/^\/\//.test(u)) ? u : '';
  }
  function money(n) {
    n = Number(n);
    if (!isFinite(n)) return '';
    var frac = Math.round(n * 100) % 100 !== 0;
    return n.toLocaleString('en-US', { minimumFractionDigits: frac ? 2 : 0, maximumFractionDigits: 2 });
  }
  function lines(s) {
    return String(s || '').split(/\n+/).map(function (x) { return x.trim(); }).filter(Boolean);
  }
  function el(html) {
    var t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  }
  function both(en, ar) {
    return esc(en) + (ar ? '<span class="of-ar">' + esc(ar) + '</span>' : '');
  }
  var ICON_ARROW = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  var ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';
  function btn(href, en, ar, cls, ext) {
    return '<a class="of-btn ' + (cls || '') + '" href="' + esc(href) + '"' + (ext ? ' target="_blank" rel="noopener"' : '') + '><span>' + esc(en) + '<small>' + esc(ar) + '</small></span>' + ICON_ARROW + '</a>';
  }
  function getJSON(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { Accept: 'application/json' } }, opts || {}))
      .then(function (r) { return r.json().catch(function () { return null; }).then(function (b) { return { status: r.status, body: b }; }); });
  }
  var cssDone = false;
  function loadAssets() {
    if (cssDone) return;
    cssDone = true;
    var f = document.createElement('link');
    f.rel = 'stylesheet';
    f.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Alexandria:wght@500;700;800&family=Permanent+Marker&display=swap';
    document.head.appendChild(f);
    if (cfg.css) {
      var c = document.createElement('link');
      c.rel = 'stylesheet';
      c.href = cfg.css;
      document.head.appendChild(c);
    }
  }

  /* ---------- countdown (one ticker for every clock on the page) ---------- */
  function left(end) {
    var ms = Math.max(0, new Date(end).getTime() - Date.now());
    var s = Math.floor(ms / 1000);
    return { ms: ms, d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function boxes(end) {
    var p = [['d', 'Days', 'يوم'], ['h', 'Hrs', 'ساعة'], ['m', 'Min', 'دقيقة'], ['s', 'Sec', 'ثانية']];
    return '<div class="of-cd" data-of-cd="boxes" data-end="' + esc(end) + '" role="timer" aria-label="Time left">' +
      p.map(function (x) { return '<div><b data-u="' + x[0] + '">00</b><span>' + x[1] + '</span><span class="of-ar">' + x[2] + '</span></div>'; }).join('') + '</div>';
  }
  function short(end) {
    return '<span data-of-cd="short" data-end="' + esc(end) + '"></span>';
  }
  var ticking = false;
  function tick() {
    var ended = false;
    document.querySelectorAll('[data-of-cd]').forEach(function (n) {
      var t = left(n.getAttribute('data-end'));
      if (t.ms <= 0) ended = true;
      if (n.getAttribute('data-of-cd') === 'short') {
        n.textContent = t.d + 'd ' + pad(t.h) + 'h ' + pad(t.m) + 'm';
      } else {
        ['d', 'h', 'm', 's'].forEach(function (u) { var b = n.querySelector('[data-u="' + u + '"]'); if (b) b.textContent = pad(t[u]); });
      }
    });
    if (ended) { teardown(); return; }
    window.setTimeout(tick, 1000);
  }
  function startTicker() { if (!ticking) { ticking = true; tick(); } }

  /** The offer ended while the page was open: take every offer piece away. */
  function teardown() {
    document.querySelectorAll('[data-of-piece]').forEach(function (n) { n.remove(); });
    document.querySelectorAll('.of-plan,.of-svc,.of-course').forEach(function (n) { n.classList.remove('of-plan', 'of-svc', 'of-course'); });
    var slot = document.querySelector('[data-offer-banner]');
    if (slot) slot.hidden = true;
    if (document.querySelector('[data-offer-page]')) window.location.reload();
  }

  /* ---------- data ---------- */
  // Dashboard preview: ?offer_preview=<id> shows an offer that is still switched
  // off (kept for this tab only, so every page of the test theme shows it).
  var preview = '';
  try {
    var pm = /[?&]offer_preview=([a-z0-9]{20,40})/.exec(window.location.search);
    if (pm) window.sessionStorage.setItem('yd.offers.preview', pm[1]);
    preview = window.sessionStorage.getItem('yd.offers.preview') || '';
  } catch (e) { /* private mode */ }
  function loadOffers() {
    if (preview) {
      return getJSON(cfg.proxy + '/public/offers?preview=' + encodeURIComponent(preview)).then(function (r) {
        return r.status === 200 && r.body ? r.body : { current: null, past: [] };
      }, function () { return { current: null, past: [] }; });
    }
    try {
      var c = JSON.parse(window.sessionStorage.getItem(CACHE) || 'null');
      if (c && Date.now() - c.at < 60000) return Promise.resolve(c.data);
    } catch (e) { /* private mode */ }
    return getJSON(cfg.proxy + '/public/offers').then(function (r) {
      var data = r.status === 200 && r.body ? r.body : { current: null, past: [] };
      try { window.sessionStorage.setItem(CACHE, JSON.stringify({ at: Date.now(), data: data })); } catch (e) { /* ignore */ }
      return data;
    }, function () { return { current: null, past: [] }; });
  }
  function dealOf(o, kind) {
    return (o.deals || []).filter(function (d) { return d.kind === kind; })[0] || null;
  }
  function mainDeals(o) {
    return (o.deals || []).filter(function (d) { return d.kind !== 'courses'; });
  }
  function words(title) {
    var w = String(title || '').trim().split(/\s+/);
    return { k: w[0] || '', t: w.slice(1).join(' ') };
  }

  /* ---------- 1. top bar ---------- */
  function topbar(o) {
    if (cfg.template === 'page' && document.querySelector('[data-offer-page]')) return;
    if (document.body.classList.contains('djsamo')) return djsamoBar(o);
    var a = el('<a class="of-topbar" data-of-piece href="' + esc(urls.offers) + '">' +
      '<span class="of-topbar__en">' + esc(o.title) + '</span>' +
      '<span class="of-topbar__time">' + short(o.endsAt) + '</span>' +
      '<span class="of-topbar__ar of-ar">' + esc(o.title_ar || '') + '</span></a>');
    document.body.insertBefore(a, document.body.firstChild);
  }

  /* ---------- 1b. floating badge (every page, always on screen) ---------- */
  // Bottom-right on purpose: the currency picker owns bottom-left. Lifted
  // above the global player via --global-player-h (set by theme.js).
  function floater(o) {
    if (document.querySelector('[data-offer-page]')) return;
    var a = el('<a class="of-float" data-of-piece href="' + esc(urls.offers) + '" aria-label="' + esc(o.title) + '">' +
      '<span class="of-float__txt"><b>' + esc(o.title) + '</b>' +
      (o.title_ar ? '<span class="of-ar">' + esc(o.title_ar) + '</span>' : '') + '</span>' +
      '<span class="of-float__time">' + short(o.endsAt) + '</span></a>');
    document.body.appendChild(a);
  }

  /* ---------- 2. homepage ticket ---------- */
  function ticket(o) {
    if (cfg.template !== 'index') return;
    var w = words(o.title);
    var n = mainDeals(o).length;
    var a = el('<a class="of-ticket" data-of-piece href="' + esc(urls.offers) + '" aria-label="' + esc(o.title) + '">' +
      '<span class="of-ticket__string"></span><span class="of-ticket__card">' +
      '<span class="of-ticket__hole" style="display:block"></span>' +
      '<span class="of-ticket__k" style="display:block">' + esc(w.k) + '</span>' +
      '<span class="of-ticket__t" style="display:block">' + esc(w.t) + '</span>' +
      (o.title_ar ? '<span class="of-ticket__a of-ar" style="display:block">' + esc(o.title_ar) + '</span>' : '') +
      '<span class="of-ticket__n" style="display:block">' + n + ' deals</span>' +
      '<span class="of-ticket__a of-ar" style="display:block;font-size:10px">' + n + ' عروض</span>' +
      '</span></a>');
    document.body.appendChild(a);
    function place() {
      var anchor = document.querySelector('.djsamo-strip') || document.querySelector('.site-header, header');
      if (!anchor) return;
      a.style.top = Math.round(anchor.getBoundingClientRect().bottom + window.scrollY) + 'px';
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('load', place);
  }

  /* ---------- 3. homepage banner ---------- */
  function banner(o) {
    var slot = document.querySelector('[data-offer-banner]');
    var inner = slot && slot.querySelector('[data-offer-banner-inner]');
    if (!inner) return;
    var deals = mainDeals(o);
    var img = slot.getAttribute('data-image') || (deals.filter(function (d) { return d.image; })[0] || {}).image || '';
    inner.innerHTML = '<div class="of-banner">' +
      (img ? '<img class="of-banner__img" src="' + esc(img) + '" alt="" loading="lazy">' : '') +
      '<div class="of-banner__shade"></div><div class="of-banner__body">' +
      '<div class="of-chips"><span class="of-chip">' + esc(o.title) + '</span>' + (o.title_ar ? '<span class="of-chip of-ar">' + esc(o.title_ar) + '</span>' : '') + '</div>' +
      '<span class="of-mk">Ends soon!</span><div class="of-banner__spacer"></div>' +
      '<h2 class="of-H of-banner__title">' + esc(o.title) + '</h2>' +
      (o.title_ar ? '<p class="of-HA of-banner__title-ar">' + esc(o.title_ar) + '</p>' : '') +
      '<p class="of-note">' + esc(o.subtitle) + (o.subtitle_ar ? '<br><span class="of-ar">' + esc(o.subtitle_ar) + '</span>' : '') + '</p>' +
      boxes(o.endsAt) + btn(urls.offers, 'Grab your deal', 'احصل على عرضك') + '</div></div>';
    slot.hidden = false;
  }

  /* ---------- 4a. DJ Samo strip (theme header) ---------- */
  function strip(o) {
    var s = document.querySelector('[data-offer-strip]');
    var c = dealOf(o, 'courses');
    if (!s || !c || !c.percentOff) return;
    var b = s.querySelector('.djsamo-strip__text b');
    var sub = s.querySelector('.djsamo-strip__sub');
    var mon = new Date(o.startsAt).toLocaleString(AR ? 'ar' : 'en-US', { month: AR ? 'long' : 'short', timeZone: 'Asia/Dubai' });
    if (b) {
      b.textContent = AR ? 'خصم ' + c.percentOff + '% على كل الدورات' : c.percentOff + '% off all DJ Samo courses';
      b.appendChild(el('<span class="of-strip-chip" data-of-piece>' + esc(AR ? mon : mon.toUpperCase()) + '</span>'));
    }
    if (sub) sub.textContent = AR ? 'هذا الشهر فقط' : 'This month only';
  }

  /* ---------- 4b. DJ Samo page ---------- */
  function djsamoBar(o) {
    var c = dealOf(o, 'courses');
    if (!c || !c.percentOff) return;
    var a = el('<a class="of-djsamo-bar" data-of-piece href="#offers"><span>' + esc(o.title) + ': ' + c.percentOff + '% off every course</span>' +
      '<span class="of-ar">' + esc(o.title_ar || '') + ': خصم ' + c.percentOff + '% على كل الدورات</span><span>' + short(o.endsAt) + '</span></a>');
    document.body.insertBefore(a, document.body.firstChild);
  }
  function courses(o) {
    var wrap = document.querySelector('[data-offer-courses]');
    var c = dealOf(o, 'courses');
    if (!wrap || !c) return;
    (c.courses || []).forEach(function (x) {
      var card = wrap.querySelector('[data-course="' + x.key + '"]');
      if (!card) return;
      card.classList.add('of-course');
      card.insertBefore(el('<span class="of-stk of-stk--y" data-of-piece>-' + esc(c.percentOff || Math.round(100 - (x.priceAed / x.oldPriceAed) * 100)) + '%</span>'), card.firstChild);
      var b = card.querySelector('.price b');
      if (b) {
        var txt = b.textContent;
        b.innerHTML = '<span class="of-old of-course__was" data-of-piece>' + esc(money(x.oldPriceAed)) + '</span>' + esc(txt.replace(/[\d.,]+/, money(x.priceAed)));
      }
      var pay = safeHref(x.payUrl);
      var payBtn = card.querySelector('.ctas a.btn:not(.btn--wa)');
      if (pay && payBtn) payBtn.href = pay;
    });
  }

  /* ---------- 4c. VIP page ---------- */
  function vip(o) {
    var plans = document.querySelector('.vip-plans');
    if (!plans) return;
    var credits = o.vipCredits || {};
    plans.parentNode.insertBefore(el('<a class="of-vip-bar" data-of-piece href="' + esc(urls.offers) + '"><span><b>' + esc(o.title) + ' is on</b>' +
      (o.title_ar ? '<span class="of-ar">' + esc(o.title_ar) + ' بدأ</span>' : '') + '</span><span class="of-vip-bar__time">' + short(o.endsAt) + '</span></a>'), plans);
    [['vip_monthly', 'monthly'], ['vip_biannual', 'biannual']].forEach(function (pair) {
      var d = dealOf(o, pair[0]);
      var card = plans.querySelector('.vip-plan-card[data-plan="' + pair[1] + '"]');
      if (!d || !card) return;
      var n = credits[pair[1]] || d.freeSongs;
      card.classList.add('of-plan');
      // A badge ("Best value") sits INSIDE the ribbon: a floating sticker
      // collided with the ribbon text and the site's Save pill.
      var ribbon = d.badge
        ? '<span class="of-plan__ribbon of-plan__ribbon--badge" data-of-piece><span class="of-plan__rt"><span>' + esc(o.title) + '</span><span class="of-ar">' + esc(o.title_ar || '') + '</span></span>' +
          '<span class="of-plan__badge">' + both(d.badge, d.badge_ar) + '</span></span>'
        : '<span class="of-plan__ribbon" data-of-piece><span>' + esc(o.title) + '</span><span class="of-ar">' + esc(o.title_ar || '') + '</span></span>';
      card.insertBefore(el(ribbon), card.firstChild);
      if (n) {
        var gift = el('<span class="of-plan__gift" data-of-piece><span>+' + n + ' free songs' + (o.creditMonths ? ', use within ' + o.creditMonths + (o.creditMonths > 1 ? ' months' : ' month') : '') +
          '<span class="of-ar">مع ' + n + ' أغنية مجانًا</span></span></span>');
        var before = card.querySelector('[data-plan-subscribe], .vip-plan-card__subscribe');
        card.insertBefore(gift, before || null);
      }
    });
  }

  /* ---------- 4d. Services page ---------- */
  function services(o) {
    var root = document.querySelector('[data-services]');
    if (!root) return;
    var deals = {};
    ['voice_tag', 'mini_mix'].forEach(function (k) { var d = dealOf(o, k); if (d && d.priceAed) deals[k] = d; });
    Object.keys(deals).forEach(function (k) {
      var card = root.querySelector('[data-service-card="' + k + '"]');
      var d = deals[k];
      if (!card) return;
      card.classList.add('of-svc');
      card.appendChild(el('<span class="of-stk" data-of-piece>' + both(d.badge || d.title, d.badge_ar || d.title_ar) + '</span>'));
      // A fixed-price deal is ordered, not quoted.
      card.querySelectorAll('.services-card__cta').forEach(function (cta) {
        if (!cta.hasAttribute('data-of-label')) cta.setAttribute('data-of-label', cta.textContent);
        cta.textContent = AR ? 'اطلب الآن' : 'Order now';
        cta.setAttribute('aria-label', cta.textContent);
      });
    });
    function choice(key, form) {
      if (!form) return;
      var old = form.querySelector('.of-choice');
      if (old) old.remove();
      var d = deals[key];
      if (!d) { resetSubmit(); return; }
      var head = document.querySelector('[data-drawer-head="' + key + '"] .services-drawer__price');
      var normal = head ? head.textContent.trim() : '';
      var info = lines(d.info)[0] || '';
      var fs = el('<fieldset class="of-choice" data-of-piece><legend>Choose your order<span class="of-ar">اختر طلبك</span></legend>' +
        '<label class="of-opt"><input type="radio" name="order_type" value="offer" checked><span class="of-opt__dot"></span><span class="of-opt__txt">' +
        '<span class="of-opt__t">' + esc(d.title) + '</span><span class="of-opt__s of-ar">' + esc(d.title_ar) + '</span>' +
        '<span class="of-opt__s">' + esc(info) + ' Pay now on the site.</span></span>' +
        '<span class="of-opt__p">' + (d.oldPriceAed ? '<span class="of-old">' + esc(money(d.oldPriceAed)) + '</span>' : '') + '<b>' + esc(money(d.priceAed)) + '</b>AED</span></label>' +
        '<label class="of-opt"><input type="radio" name="order_type" value="normal"><span class="of-opt__dot"></span><span class="of-opt__txt">' +
        '<span class="of-opt__t">Normal order</span><span class="of-opt__s of-ar">طلب عادي</span>' +
        '<span class="of-opt__s">We confirm the price with you on WhatsApp.</span></span>' +
        '<span class="of-opt__p">' + (normal ? '<b>' + esc(normal) + '</b>' : '') + '</span></label></fieldset>');
      form.insertBefore(fs, form.firstChild);
      // The submit says "Get my free quote on WhatsApp" with a WhatsApp icon;
      // an offer order is paid on the site, so say that while it is chosen.
      var btn = form.querySelector('[data-submit]');
      var lbl = btn && btn.querySelector('[data-submit-label]');
      var icon = btn && btn.querySelector('svg');
      var fine = form.querySelector('.services-fine');
      if (btn && !btn.hasAttribute('data-of-normal')) btn.setAttribute('data-of-normal', btn.getAttribute('data-label') || '');
      if (fine && !fine.hasAttribute('data-of-normal')) fine.setAttribute('data-of-normal', fine.textContent);
      var payLabel = AR ? 'اطلب وادفع الآن' : 'Order and pay now';
      var payFine = AR ? 'تدفع على الموقع بعد إرسال النموذج. سعر ثابت.' : 'You pay on the site right after you send the form. Fixed price.';
      function sync() {
        var offer = !!fs.querySelector('input[value="offer"]:checked');
        if (btn) btn.setAttribute('data-label', offer ? payLabel : btn.getAttribute('data-of-normal'));
        if (lbl) lbl.textContent = btn.getAttribute('data-label');
        if (icon) icon.style.display = offer ? 'none' : '';
        if (btn) { btn.classList.toggle('services-btn--gold', offer); btn.classList.toggle('services-btn--wa', !offer); }
        if (fine) fine.textContent = offer ? payFine : fine.getAttribute('data-of-normal');
      }
      fs.addEventListener('change', sync);
      sync();
    }
    function resetSubmit() {
      var form = document.querySelector('[data-services-form]');
      if (!form) return;
      var btn = form.querySelector('[data-submit][data-of-normal]');
      if (btn) {
        btn.setAttribute('data-label', btn.getAttribute('data-of-normal'));
        var l = btn.querySelector('[data-submit-label]'); if (l) l.textContent = btn.getAttribute('data-label');
        var i = btn.querySelector('svg'); if (i) i.style.display = '';
        btn.classList.remove('services-btn--gold'); btn.classList.add('services-btn--wa');
      }
      var fine = form.querySelector('.services-fine[data-of-normal]');
      if (fine) fine.textContent = fine.getAttribute('data-of-normal');
    }
    // The drawer head says "Free quote on WhatsApp" and shows the normal
    // "from" price; signed-out visitors (most of them, from the ads) never see
    // the choice inside the form. For an offer service, show the deal there.
    var kicker = document.querySelector('.services-drawer__kicker');
    var kickerText = kicker ? kicker.innerHTML : '';
    function head(key) {
      document.querySelectorAll('.of-dhead').forEach(function (n) { n.remove(); });
      var d = deals[key];
      if (kicker) kicker.innerHTML = d ? 'Order now <span class="of-ar">اطلب الآن</span>' : kickerText;
      if (!d) return;
      var meta = document.querySelector('[data-drawer-head="' + key + '"]');
      if (!meta) return;
      meta.hidden = true; // the normal "from" price + "free quote" line
      var first = d.description || lines(d.info)[0] || '';
      var firstAr = d.description_ar || lines(d.info_ar)[0] || '';
      var box = el('<div class="of-dhead" data-of-piece>' +
        '<span class="of-dhead__t">' + esc(d.title) + ' <span class="of-ar">' + esc(d.title_ar) + '</span></span>' +
        '<span class="of-dhead__p">' + (d.oldPriceAed ? '<span class="of-old">' + esc(money(d.oldPriceAed)) + '</span> ' : '') + '<b>' + esc(money(d.priceAed)) + ' AED</b></span>' +
        (first ? '<span class="of-dhead__s">' + esc(first) + '</span>' : '') +
        '<span class="of-dhead__s">Fixed price. Fill in the short form, then pay on the site.</span>' +
        (firstAr ? '<span class="of-dhead__s of-ar">' + esc(firstAr) + '</span>' : '') +
        '<span class="of-dhead__s of-ar">سعر ثابت. املأ النموذج القصير ثم ادفع على الموقع.</span></div>');
      meta.parentNode.insertBefore(box, meta.nextSibling);
    }
    root.addEventListener('services:select', function (e) { choice(e.detail.key, e.detail.form); head(e.detail.key); });
    root.addEventListener('services:offer-ended', function () {
      document.querySelectorAll('.of-choice,.of-svc .of-stk,.of-dhead').forEach(function (n) { n.remove(); });
      document.querySelectorAll('[data-of-label]').forEach(function (n) { n.textContent = n.getAttribute('data-of-label'); n.removeAttribute('data-of-label'); });
      if (kicker) kicker.innerHTML = kickerText;
      resetSubmit();
      deals = {};
    });
    var cur = root.getAttribute('data-current-service');
    if (cur) { choice(cur, document.querySelector('[data-services-form]')); head(cur); }
  }

  /* ---------- 5. sale page ---------- */
  var COURSE_INFO = {
    one: ['One Class', 'درس واحد', 'Single class', 'درس منفرد', [['1 lesson, 2 hours', 'درس واحد، ساعتان'], ['You choose the topic', 'أنت تختار الموضوع'], ['Recording included', 'التسجيل مشمول']]],
    dj: ['DJ Course', 'دورة الدي جي', 'Rekordbox + Serato', 'Rekordbox + Serato', [['6 lessons, 2 hours each', '6 دروس، ساعتان لكل درس'], ['2 follow-up sessions, 30 min', 'جلستا متابعة، 30 دقيقة'], ['Final exam and certificate', 'اختبار نهائي وشهادة']]],
    pr: ['Remix Production', 'إنتاج الريمكس', 'Ableton Live', 'Ableton Live', [['6 lessons, 2 hours each', '6 دروس، ساعتان لكل درس'], ['Full sound library included', 'مكتبة صوتية كاملة مشمولة'], ['Recording of every lesson', 'تسجيل كل درس يبقى ملكك']]]
  };
  function li(en, ar) {
    return '<div class="of-li"><span class="of-li__dot">' + ICON_CHECK + '</span><span>' + esc(en) + (ar ? '<span class="of-ar">' + esc(ar) + '</span>' : '') + '</span></div>';
  }
  function rows(en, ar) {
    var out = '';
    for (var k = 0; k < Math.max(en.length, ar.length); k++) out += li(en[k] || '', ar[k] || '');
    return out;
  }
  function dealCta(d) {
    if (d.kind === 'vip_monthly') return [urls.vip + '?resume=monthly', 'Join VIP', 'اشترك في VIP'];
    if (d.kind === 'vip_biannual') return [urls.vip + '?resume=biannual', 'Join VIP', 'اشترك في VIP'];
    if (d.kind === 'voice_tag' || d.kind === 'mini_mix') return [urls.services + '?service=' + d.kind, 'Grab your deal', 'احصل على عرضك'];
    var u = safeHref(d.ctaUrl);
    return u ? [u, 'Grab your deal', 'احصل على عرضك'] : null;
  }
  function priceBlock(d, o) {
    if (d.priceAed == null) return '';
    var vipPlan = d.kind === 'vip_monthly' ? 'monthly' : d.kind === 'vip_biannual' ? 'biannual' : '';
    var save = '';
    if (vipPlan) {
      var n = (o.vipCredits || {})[vipPlan] || d.freeSongs;
      if (n) save = '+' + n + ' free songs<br><span class="of-ar">مع ' + n + ' أغنية مجانًا</span>';
    } else if (d.oldPriceAed && d.oldPriceAed > d.priceAed) {
      var s = money(d.oldPriceAed - d.priceAed);
      save = 'Save ' + esc(s) + ' AED<br><span class="of-ar">وفّر ' + esc(s) + ' درهم</span>';
    }
    var per = d.kind === 'vip_monthly' ? 'per month' : d.kind === 'vip_biannual' ? '6 months' : '';
    var perAr = d.kind === 'vip_monthly' ? 'شهريًا' : d.kind === 'vip_biannual' ? '6 أشهر' : '';
    return '<div class="of-pr"><div class="of-pr__main">' +
      (d.oldPriceAed && d.oldPriceAed > d.priceAed && !vipPlan ? '<span class="of-old" style="font-size:18px">' + esc(money(d.oldPriceAed)) + '</span><span class="of-y">' + ICON_ARROW + '</span>' : '') +
      '<span class="of-price" style="font-size:40px">' + esc(money(d.priceAed)) + '</span>' +
      '<span class="of-pr__cur">AED' + (per ? ' ' + per : '') + '<br><span class="of-ar">درهم' + (perAr ? ' ' + perAr : '') + '</span></span></div>' +
      (save ? '<div class="of-pr__save">' + save + '</div>' : '') + '</div>';
  }
  /* focus = the deal an ad linked to (?deal=): price + button sit right under the
     title so they show on the first phone screen; the details follow below. */
  function dealCard(d, i, o, focus) {
    var en = lines(d.info);
    var ar = lines(d.info_ar);
    var cta = dealCta(d);
    var num = i + 1;
    var buy = priceBlock(d, o) + (cta ? btn(cta[0], cta[1], cta[2]) : '');
    return '<section class="of-deal' + (focus ? ' of-deal--focus' : '') + '" id="deal-' + num + '"><div class="of-deal__media">' +
      (d.image ? '<img src="' + esc(d.image) + '" alt=""' + (focus ? ' fetchpriority="high"' : ' loading="lazy"') + '>' : '') +
      '<div class="of-chips"><span class="of-chip">Deal ' + pad(num) + '</span><span class="of-chip of-ar">العرض ' + num + '</span></div>' +
      (d.badge ? '<span class="of-stk">' + both(d.badge, d.badge_ar) + '</span>' : '') + '</div>' +
      '<div class="of-deal__body"><div><h3 class="of-H of-deal__title">' + esc(d.title) + '</h3>' +
      (d.title_ar ? '<p class="of-HA of-deal__title-ar">' + esc(d.title_ar) + '</p>' : '') + '</div>' +
      (focus ? buy : '') +
      (d.description || d.description_ar ? '<div class="of-what"><span class="of-lbl">What is it <span class="of-ar">ما هو العرض</span></span>' +
        (d.description ? '<p class="of-note">' + esc(d.description) + '</p>' : '') + (d.description_ar ? '<p class="of-note of-ar">' + esc(d.description_ar) + '</p>' : '') + '</div>' : '') +
      (en.length || ar.length ? '<div style="display:flex;flex-direction:column;gap:8px"><span class="of-lbl">Good to know <span class="of-ar">معلومات مهمة</span></span>' +
        rows(en, ar) + '</div>' : '') +
      (focus ? '' : buy) + '</div></section>';
  }
  function academy(c) {
    if (!c || !c.courses || !c.courses.length) return '';
    var waText = encodeURIComponent('Hi DJ Samo, I want the ' + (c.percentOff || '') + '% course offer');
    return '<section class="of-academy" id="academy"><div class="of-lbl" style="text-align:center">Also this month <span class="of-ar">وأيضًا هذا الشهر</span></div>' +
      '<div class="of-academy__head">' + (cfg.djsamoImg ? '<img class="of-academy__img" src="' + esc(cfg.djsamoImg) + '" alt="DJ Samo" loading="lazy">' : '') +
      '<div class="of-academy__txt"><span class="of-lbl" style="color:#fff">DJ Samo Academy <span class="of-ar">أكاديمية دي جي سامو</span></span>' +
      '<h2 class="of-H of-academy__title">' + esc(c.percentOff) + '% off <span class="of-y">all courses</span></h2>' +
      '<p class="of-HA" style="font-size:22px;color:#fff">خصم ' + esc(c.percentOff) + '% على <span class="of-y">كل الدورات</span></p>' +
      (c.description ? '<p class="of-note" style="color:#fff">' + esc(c.description) + '</p>' : '') + '</div></div><div class="of-academy__cards">' +
      c.courses.map(function (x) {
        var info = COURSE_INFO[x.key];
        if (!info) return '';
        var save = money(x.oldPriceAed - x.priceAed);
        var pay = safeHref(x.payUrl) || (urls.djsamo + '#offers');
        return '<div class="of-ccard"><span class="of-stk of-stk--y">-' + esc(c.percentOff) + '%</span>' +
          '<div class="of-chips"><span class="of-chip">' + esc(info[2]) + '</span><span class="of-chip of-ar" style="color:#fff">' + esc(info[3]) + '</span></div>' +
          '<div><h3 class="of-H" style="font-size:26px;color:#fff">' + esc(info[0]) + '</h3><p class="of-HA" style="font-size:18px;color:var(--of-y);margin-top:4px">' + esc(info[1]) + '</p></div>' +
          '<div style="display:flex;flex-direction:column;gap:8px">' + info[4].map(function (f) { return li(f[0], f[1]); }).join('') + '</div>' +
          '<div class="of-pr"><div class="of-pr__main"><span class="of-old" style="font-size:16px">' + esc(money(x.oldPriceAed)) + '</span><span class="of-y">' + ICON_ARROW + '</span>' +
          '<span class="of-price" style="font-size:34px">' + esc(money(x.priceAed)) + '</span><span class="of-pr__cur">AED<br><span class="of-ar">درهم</span></span></div>' +
          '<div class="of-pr__save">Save ' + esc(save) + '<br><span class="of-ar">وفّر ' + esc(save) + '</span></div></div>' +
          btn(pay, 'Pay by card', 'ادفع بالبطاقة', 'of-btn--red', /^https:/.test(pay)) +
          '<a class="of-btn of-btn--ghost" style="min-height:44px;font-size:12px;color:#fff;border-color:#5a2a2a" href="https://wa.me/' + WA + '?text=' + waText + '" target="_blank" rel="noopener">Ask on WhatsApp <span class="of-ar">اسأل على واتساب</span></a></div>';
      }).join('') + '</div></section>';
  }
  function missed(past) {
    if (!past || !past.length) return '';
    return '<section class="of-missed"><h2 class="of-H" style="font-size:26px;color:#8d8585">Offers you missed</h2>' +
      '<p class="of-HA" style="font-size:18px;color:#8d8585;margin:4px 0 14px">عروض فاتتك</p><div class="of-missed__grid">' +
      past.map(function (p) {
        var m = new Date(p.endsAt).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'Asia/Dubai' });
        return '<div class="of-past"><div class="of-lbl" style="color:#9a9090">' + esc(m) + '</div><div class="of-past__t">' + esc(p.title) + '</div>' +
          (p.title_ar ? '<div class="of-ar" style="font-weight:700;font-size:14px;color:#b0a6a6">' + esc(p.title_ar) + '</div>' : '') +
          '<div style="font-size:12px;color:#9a9090;margin-top:6px">' + (p.deals || []).map(function (d) { return esc(d.title); }).join('<br>') + '</div>' +
          '<span class="of-past__stamp">ENDED<br><span class="of-ar">انتهى</span></span></div>';
      }).join('') + '</div><p class="of-note" style="text-align:center;margin-top:16px">Follow us so you don\'t miss next month.<br><span class="of-ar">تابعنا حتى لا يفوتك عرض الشهر الجاي.</span></p></section>';
  }
  /* ?deal=mini_mix (deal kind) or ?deal=2 (position) → that deal's index, else -1 */
  function focusIndex(deals) {
    var q;
    try { q = new URLSearchParams(location.search).get('deal'); } catch (e) { q = null; }
    if (!q) return -1;
    for (var i = 0; i < deals.length; i++) if (deals[i].kind === q) return i;
    var n = parseInt(q, 10);
    return String(n) === q && n >= 1 && n <= deals.length ? n - 1 : -1;
  }
  /* the deals are drawn after load, so the browser never finds #deal-N on its own */
  function scrollToHash() {
    var id = (location.hash || '').slice(1);
    if (!/^(deal-\d+|academy)$/.test(id)) return;
    var t = document.getElementById(id);
    if (t) t.scrollIntoView();
  }
  function salePage(data) {
    var root = document.querySelector('[data-offer-page]');
    if (!root) return;
    var o = data.current;
    if (!o) {
      root.innerHTML = '<div class="of-empty"><h1 class="of-H" style="font-size:34px;color:#fff">No offer right now</h1>' +
        '<p class="of-HA" style="font-size:22px;color:var(--of-y)">لا يوجد عرض حاليًا</p>' +
        '<p class="of-note">A new offer comes every month. Follow us so you don\'t miss it.<br><span class="of-ar">كل شهر عرض جديد. تابعنا حتى لا يفوتك.</span></p>' +
        btn(urls.store || '/collections/all', 'Browse the store', 'تصفّح المتجر') + '</div>' + missed(data.past);
      return;
    }
    var deals = mainDeals(o);
    var c = dealOf(o, 'courses');
    var nav = deals.map(function (d, i) { return '<a class="of-chip" href="#deal-' + (i + 1) + '">' + esc(d.title) + '</a>'; }).join('') +
      (c && c.courses && c.courses.length ? '<a class="of-chip" href="#academy">' + esc(c.title || 'Courses') + '</a>' : '');
    var fi = focusIndex(deals);
    if (fi >= 0) {
      /* ad landing (?deal=): short header, the linked deal first, the rest below */
      root.innerHTML = '<div class="of-hero of-hero--focus"><div class="of-hero__in">' +
        '<p class="of-H of-hero__title">' + esc(o.title) + (o.title_ar ? ' <span class="of-ar">' + esc(o.title_ar) + '</span>' : '') + '</p>' +
        boxes(o.endsAt) + '</div></div>' +
        '<div class="of-deals">' + dealCard(deals[fi], fi, o, true) + '</div>' +
        '<nav class="of-nav" aria-label="Jump to a deal">' + nav + '</nav>' +
        '<div class="of-lbl of-more">More offers <span class="of-ar">عروض أخرى</span></div>' +
        '<div class="of-deals">' + deals.map(function (d, i) { return i === fi ? '' : dealCard(d, i, o); }).join('') + '</div>' +
        academy(c) + missed(data.past);
      return;
    }
    root.innerHTML = '<div class="of-hero"><div class="of-hero__in">' +
      '<div class="of-chips" style="justify-content:center"><span class="of-chip">' + deals.length + ' offers</span><span class="of-chip of-ar">' + deals.length + ' عروض</span></div>' +
      '<h1 class="of-H of-hero__title">' + esc(o.title) + '</h1>' +
      (o.title_ar ? '<p class="of-HA of-hero__title-ar">' + esc(o.title_ar) + '</p>' : '') +
      '<p class="of-note">' + esc(o.subtitle) + (o.subtitle_ar ? '<br><span class="of-ar">' + esc(o.subtitle_ar) + '</span>' : '') + '</p>' +
      boxes(o.endsAt) + '</div></div>' +
      '<nav class="of-nav" aria-label="Jump to a deal">' + nav + '</nav>' +
      '<div class="of-deals">' + deals.map(function (d, i) { return dealCard(d, i, o); }).join('') + '</div>' +
      academy(c) + missed(data.past);
  }

  /* ---------- 6. credits (independent of the offer) ---------- */
  function credits() {
    var btnSlot = document.querySelector('[data-offer-credit]');
    var card = document.querySelector('[data-offer-credits-card]');
    if (!cfg.signedIn || (!btnSlot && !card)) return;
    getJSON(cfg.proxy + '/offers/shopify/credits').then(function (r) {
      var c = r.status === 200 && r.body ? r.body.credits : null;
      if (!c) return;
      loadAssets();
      if (card) creditsCard(card, c);
      if (btnSlot && c.left > 0) creditButton(btnSlot, c);
    }, function () { /* offline: show nothing */ });
  }
  function fmtDate(iso) {
    return new Date(iso).toLocaleDateString(AR ? 'ar' : 'en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Dubai' });
  }
  function daysLeft(iso) { return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)); }
  function creditsCard(node, c) {
    var pct = c.granted ? Math.round((c.left / c.granted) * 100) : 0;
    var d = daysLeft(c.expiresAt);
    node.innerHTML = '<div class="of-cc"><span class="of-stk">VIP bonus<span class="of-ar">مكافأة VIP</span></span>' +
      '<span class="of-lbl">Song credits <span class="of-ar">رصيد الأغاني</span></span><div class="of-cc__row">' +
      '<div class="of-ring" style="background:conic-gradient(var(--of-y) 0 ' + pct + '%,#2a1a1a ' + pct + '% 100%)"><div class="of-ring__in"><b>' + c.left + '</b><span>of ' + c.granted + ' left</span></div></div>' +
      '<div style="display:flex;flex-direction:column;gap:6px"><div style="font-weight:900;font-size:15px">' + c.left + ' free songs left</div>' +
      '<div class="of-ar" style="font-size:13px;color:var(--of-muted)">باقي لك ' + c.left + ' أغاني مجانًا</div>' +
      '<div class="of-cc__exp">Expires in ' + d + (d === 1 ? ' day' : ' days') + ' (' + esc(fmtDate(c.expiresAt)) + ')</div>' +
      '<div class="of-cc__exp of-ar">ينتهي خلال ' + d + ' يومًا</div></div></div>' +
      (c.left > 0 ? btn(urls.store || '/collections/all', 'Pick songs in the store', 'اختر أغانيك من المتجر') : '') +
      (c.songs && c.songs.length ? '<div class="of-cc__used"><span class="of-lbl">Used credits <span class="of-ar">الأغاني المستخدمة</span></span>' +
        c.songs.map(function (s) {
          var t = s.handle ? '<a href="/products/' + encodeURIComponent(s.handle) + '">' + esc(s.title) + '</a>' : esc(s.title);
          return '<div>' + t + '<span>' + esc(fmtDate(s.takenAt)) + '</span></div>';
        }).join('') + '</div>' : '') + '</div>';
    node.hidden = false;
  }
  function creditButton(node, c) {
    var pid = node.getAttribute('data-product-id');
    var msgs = {
      already_owned: ['You already have this song in your downloads.', 'هذه الأغنية موجودة في تحميلاتك.'],
      already_taken: ['You already took this song with a credit.', 'أخذت هذه الأغنية برصيدك من قبل.'],
      no_credits: ['No free songs left.', 'لم يتبقَّ لك أغانٍ مجانية.'],
      login: ['Please log in again.', 'يرجى تسجيل الدخول مرة أخرى.'],
      error: ['Something went wrong. No credit was used. Please try again.', 'حدث خطأ. لم يُخصم أي رصيد. حاول مرة أخرى.']
    };
    function idle(left) {
      node.innerHTML = '<div class="of-credit"><p class="of-credit__line">You have <b>' + left + ' free songs</b> until ' + esc(fmtDate(c.expiresAt)) +
        '<span class="of-ar">لديك ' + left + ' أغاني مجانية</span></p><div class="of-credit__row">' +
        '<button type="button" class="of-btn" data-use><span>Use 1 credit<small>استخدم رصيد أغنية</small></span></button></div></div>';
      node.querySelector('[data-use]').addEventListener('click', confirmStep);
    }
    function confirmStep() {
      node.querySelector('.of-credit__row').innerHTML = '<button type="button" class="of-btn" data-yes><span>Yes, take it free<small>نعم، خذها مجانًا</small></span></button>' +
        '<button type="button" class="of-btn of-btn--ghost" data-no>Cancel <span class="of-ar">إلغاء</span></button>';
      node.querySelector('[data-no]').addEventListener('click', function () { idle(c.left); });
      node.querySelector('[data-yes]').addEventListener('click', redeem);
    }
    function say(kind, pair) {
      var p = node.querySelector('.of-credit__msg');
      if (!p) { p = document.createElement('p'); node.querySelector('.of-credit').appendChild(p); }
      p.className = 'of-credit__msg of-credit__msg--' + kind;
      p.setAttribute('role', 'status');
      p.innerHTML = esc(pair[0]) + '<span class="of-ar">' + esc(pair[1]) + '</span>';
    }
    function redeem(e) {
      var b = e.currentTarget;
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
      getJSON(cfg.proxy + '/offers/shopify/redeem', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: pid })
      }).then(function (r) {
        if (r.status === 200 && r.body && r.body.ok) {
          c.left = r.body.left;
          node.querySelector('.of-credit__row').innerHTML = btn(urls.account || '/account', 'Go to my downloads', 'اذهب إلى تحميلاتي');
          say('ok', ['Done! The song is in your downloads. ' + r.body.left + ' free songs left.', 'تم! الأغنية في تحميلاتك. باقي لك ' + r.body.left + '.']);
          return;
        }
        var code = r.body && r.body.code;
        var pair = r.status === 401 ? msgs.login : msgs[code] || msgs.error;
        idle(c.left);
        say('err', pair);
      }, function () { idle(c.left); say('err', msgs.error); });
    }
    loadAssets();
    idle(c.left);
    node.hidden = false;
  }

  /* ---------- boot ---------- */
  function boot() {
    credits();
    loadOffers().then(function (data) {
      var o = data && data.current;
      if (!o || new Date(o.endsAt).getTime() <= Date.now()) {
        if (document.querySelector('[data-offer-page]')) { loadAssets(); salePage({ current: null, past: (data && data.past) || [] }); }
        return;
      }
      loadAssets();
      document.documentElement.classList.add('has-offer');
      [topbar, floater, ticket, banner, strip, courses, vip, services].forEach(function (fn) {
        try { fn(o); } catch (err) { if (window.console) console.warn('[offers]', fn.name, err); }
      });
      salePage(data);
      scrollToHash();
      startTicker();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
